import { Infer, v } from "convex/values";
import { defineSchema, defineTable } from "convex/server";

import { authTables } from "@convex-dev/auth/server";

export const CURRENCIES = {
  USD: "usd",
  EUR: "eur",
  GBP: "gbp",
} as const;
export const currencyValidator = v.union(
  v.literal(CURRENCIES.USD),
  v.literal(CURRENCIES.EUR),
  v.literal(CURRENCIES.GBP),
);
export type Currency = Infer<typeof currencyValidator>;

export const appFields = {
  name: v.string(),
  description: v.string(),
  logo: v.optional(v.union(v.string(), v.id("_storage"))),
  website: v.optional(v.string()),
  legalCompanyName: v.optional(v.string()),
  legalCompanyAddress: v.optional(v.string()),
  additionalInfo: v.optional(
    v.array(
      v.object({
        title: v.string(),
        description: v.string(),
        type: v.union(v.literal("usps"), v.literal("testimonial")),
      }),
    ),
  ),
  productDemos: v.optional(v.array(v.id("_storage"))), // Array of product demo video storage IDs
  status: v.optional(v.union(v.literal("active"), v.literal("inactive"))), // To be removed
};

export const userFields = {
  name: v.optional(v.string()),
  image: v.optional(v.union(v.string(), v.id("_storage"))),
  email: v.optional(v.string()),
  emailVerificationTime: v.optional(v.number()),
  phone: v.optional(v.string()),
  phoneVerificationTime: v.optional(v.number()),
  isAnonymous: v.optional(v.boolean()),
  customerId: v.optional(v.string()), // STRIPE CUSTOMER ID MAINLY USED FOR FOUNDERS
  bankDetails: v.optional(
    v.object({
      firstName: v.string(),
      lastName: v.string(),
      homeAddress: v.string(),
      iban: v.optional(v.string()),
      accountNumber: v.string(),
      bicOrSwift: v.string(),
    }),
  ),
  contentPlatform: v.optional(
    v.object({
      daily: v.optional(
        v.array(
          v.union(
            v.literal("youtube"),
            v.literal("tiktok"),
            v.literal("instagram"),
            v.literal("youtube-shorts"),
          ),
        ),
      ),
      weekly: v.optional(
        v.array(
          v.union(
            v.literal("youtube"),
            v.literal("tiktok"),
            v.literal("instagram"),
            v.literal("youtube-shorts"),
          ),
        ),
      ),
    }),
  ),
  mainVideoChannelLink: v.optional(v.string()),
  placeOfResidence: v.optional(v.string()),
  howDidYouHearAboutUs: v.optional(
    v.union(
      v.literal("google"),
      v.literal("twitter"),
      v.literal("linkedin"),
      v.literal("facebook"),
      v.literal("instagram"),
      v.literal("tiktok"),
      v.literal("youtube"),
      v.literal("referral"),
      v.string(),
    ),
  ),
  hasBeenPaidForBrandDeal: v.optional(v.boolean()),
};

const schema = defineSchema({
  ...authTables,
  users: defineTable(userFields)
    .searchIndex("search_name", {
      searchField: "name",
    })
    .index("email", ["email"])
    .index("customerId", ["customerId"]),

  campaigns: defineTable({
    name: v.string(),
    appId: v.id("apps"),
    status: v.union(v.literal("active"), v.literal("ended")),

    // To be deprecated
    type: v.union(
      v.literal("CiR"),
      v.literal("influencer"),
      v.literal("challenge"),
    ), // CiR = Creator in Residence, Influencer = Influencer campaign, Challenge = Challenge campaign

    isPublic: v.boolean(), // if true, the campaign is public and can be seen by all creators & can be applied to by all creators
    applicationNotRequired: v.optional(v.boolean()), // if true, creators will automatically get added to the campaign

    // Video approval workflow settings
    allowCreatorsToPostDirectlyToSocials: v.optional(v.boolean()), // if true, creators can post without having to submit a video project
    aiReviewRequired: v.optional(v.boolean()), // if true, the AI will review all videos
    teamVideoReviewRequired: v.optional(v.boolean()), // if true, the campaign will require video review by the team
    manualVideoReviewRequired: v.optional(v.boolean()), // if true, the campaign will require manual review of videos by the founder
    prizePool: v.optional(v.number()), // budget set by the founder of the campaign

    allowedChannels: v.optional(
      v.array(
        v.union(
          v.literal("tiktok"),
          v.literal("youtube"),
          v.literal("youtube-shorts"),
          v.literal("instagram"),
        ),
      ),
    ),

    validationRequirements: v.optional(v.string()), // for challenge campaigns, what counts as a valid post

    creatorCriteria: v.optional(
      v.array(
        v.object({
          title: v.string(),
          description: v.string(),
          weight: v.optional(v.number()),
        }),
      ),
    ),

    videos: v.optional(v.array(v.union(v.id("_storage"), v.string()))), // url or storage id of marketing videos
    urls: v.optional(
      v.array(
        v.object({
          url: v.string(),
          title: v.string(),
        }),
      ),
    ), // urls to other marketing content (e.g. blog posts, social media posts, etc.)
    inspirationVideos: v.optional(
      v.array(v.union(v.id("_storage"), v.string())),
    ), // storage IDs or urls to past successful UGC videos from TikTok/YouTube/Instagram for inspiration

    creatorBrief: v.optional(v.string()),
    budgetCpmMin: v.optional(v.number()),
    budgetCpmMax: v.optional(v.number()),
    predefinedCpm: v.optional(v.number()), // for challenge campaigns, this is the cpm that will be used for all creators
    budgetFlatFeeMin: v.optional(v.number()),
    budgetFlatFeeMax: v.optional(v.number()),

    endDate: v.optional(v.number()),
    eligibleCountries: v.optional(v.array(v.string())), // Array of country codes (e.g., ["US", "GB", "CA"])
  })
    .index("by_app", ["appId"])
    .index("by_status_and_isPublic", ["status", "isPublic"]),

  creators: defineTable({
    userId: v.id("users"),
    bio: v.optional(v.string()),
    isPublic: v.optional(v.boolean()),
    isUGCAcceleratorMember: v.optional(v.boolean()), // Tag
    isCreatorPartner: v.optional(v.boolean()), // Tag
    isCiR: v.optional(v.boolean()), // Tag - Creator In Residence
    showLeaderboardOnHomeScreen: v.optional(v.boolean()),
  })
    .index("by_user", ["userId"])
    .index("by_isUGCAcceleratorMember", ["isUGCAcceleratorMember"])
    .index("by_isCreatorPartner", ["isCreatorPartner"])
    .index("by_isCiR", ["isCiR"])
    .index("by_isPublic", ["isPublic"]),

  creatorHooks: defineTable({
    creatorId: v.id("creators"),
    campaignId: v.optional(v.id("campaigns")),
    scriptContent: v.string(),
  })
    .index("by_creator", ["creatorId"])
    .index("by_campaign", ["campaignId"])
    .index("by_creator_and_campaign", ["creatorId", "campaignId"]),

  creatorUploadedVideos: defineTable({
    creatorId: v.id("creators"),
    videoStorageId: v.id("_storage"),
    type: v.union(v.literal("hook"), v.literal("productdemo")),
  })
    .index("by_creator", ["creatorId"])
    .index("by_creator_and_type", ["creatorId", "type"]),

  creatorAuthTokens: defineTable({
    creatorId: v.id("creators"),
    instagram: v.optional(
      v.array(
        v.object({
          handle: v.string(),
          followerCount: v.optional(v.number()),
          postCount: v.optional(v.number()),
          isPublic: v.boolean(),
          accessToken: v.string(),
          trackAutomatically: v.optional(v.array(v.id("creatorContracts"))),
        }),
      ),
    ),
    tiktok: v.optional(
      v.array(
        v.object({
          handle: v.string(),
          followerCount: v.optional(v.number()),
          postCount: v.optional(v.number()),
          isPublic: v.boolean(),
          accessToken: v.string(),
          refreshToken: v.string(),
          trackAutomatically: v.optional(v.array(v.id("creatorContracts"))),
        }),
      ),
    ),
    youtube: v.optional(
      v.array(
        v.object({
          handle: v.string(), // this is the channel name
          // TODO: add actual handle
          followerCount: v.optional(v.number()),
          postCount: v.optional(v.number()),
          isPublic: v.boolean(),
          accessToken: v.string(),
          refreshToken: v.string(),
          trackAutomatically: v.optional(v.array(v.id("creatorContracts"))),
        }),
      ),
    ),
  }).index("by_creator", ["creatorId"]),

  // Table to track creator applications (as in applying to create content for an app) to apps
  creatorApplications: defineTable({
    campaignId: v.optional(v.id("campaigns")),
    creatorId: v.id("creators"),
    appId: v.id("apps"),
    status: v.union(
      v.literal("pending"),
      v.literal("approved"),
      v.literal("rejected"),
    ),
    // Application details
    message: v.optional(v.string()), // Creator's pitch/message
    links: v.optional(v.array(v.id("_storage"))), // array of links to videos (most likely unlisted yt videos)
    // Timestamps
    respondedAt: v.optional(v.number()),
    // If rejected, optional reason
    rejectionReason: v.optional(v.string()),

    //TODO: make sure to not return this in the api unless it's the founder requesting the data
    aiAnalysis: v.optional(
      v.object({
        score: v.number(), // out of 100
        notes: v.string(),
      }),
    ),
  })
    .index("by_creator", ["creatorId"])
    .index("by_campaign", ["campaignId"])
    .index("by_campaign_and_creator", ["campaignId", "creatorId"])
    .index("by_app", ["appId"])
    .index("by_status", ["status"])
    .index("by_creator_and_app", ["creatorId", "appId"])
    .index("by_creator_and_status", ["creatorId", "status"])
    .index("by_app_and_status", ["appId", "status"]),

  creatorContractRevisions: defineTable({
    contractId: v.id("creatorContracts"),
    status: v.union(
      v.literal("pendingCreatorApproval"),
      v.literal("pendingAppFounderApproval"),
      v.literal("approved"),
      v.literal("rejected"),
    ),
    newEndDate: v.optional(v.number()),
  }).index("by_contract", ["contractId"]),

  // Table for creator contracts
  creatorContracts: defineTable({
    campaignId: v.id("campaigns"),
    creatorId: v.id("creators"),
    appId: v.id("apps"),
    // Contract status
    status: v.union(
      v.literal("pendingApproval"),
      v.literal("active"),
      v.literal("completed"),
      v.literal("terminated"),
    ),

    documents: v.optional(
      v.array(
        v.object({
          name: v.string(),
          url: v.union(v.string(), v.id("_storage")), // can be a url or uploaded file
          uploadedDate: v.number(),
          uploadedBy: v.optional(v.id("users")),
        }),
      ),
    ),

    // Contract terms
    startDate: v.number(),
    endDate: v.number(),

    currency: currencyValidator,

    requiredVideos: v.object({
      count: v.number(),
      duration: v.number(), // in days
    }),

    flatFee: v.optional(
      v.object({
        perVideo: v.optional(v.number()),
        timePeriod: v.optional(
          v.object({
            amount: v.number(),
            days: v.number(), // number of days (for full flexibility if people want to pay out every 10 days or something)
          }),
        ),
      }),
    ),

    cpmRate: v.optional(
      v.union(
        v.object({
          type: v.literal("totalViews"),
          threshold: v.number(), // 10K views
          rate: v.number(), // $2 CPM
          maxPayout: v.optional(v.number()),
          cutoffPeriod: v.number(), // this represents the number of days after the contract ends to calculate the total views to account for videos posted on the last day
        }),
        v.object({
          type: v.literal("perVideo"),
          threshold: v.number(), // 25K views
          rate: v.number(), // $3 CPM
          maxPayout: v.optional(v.number()),
          cutoffPeriod: v.number(), // this represents the number of days after a video is posted to calculate earnings
        }),
      ),
    ),

    // Payment tracking
    creatorInvoice: v.optional(v.union(v.string(), v.id("_storage"))), // url or file
    paidAmount: v.optional(v.number()),
    paymentDate: v.optional(v.number()),
    paymentReceipt: v.optional(v.union(v.string(), v.id("_storage"))), // url or file (creator)
    founderPaymentReceipt: v.optional(v.union(v.string(), v.id("_storage"))), // url or file (founder)
  })
    .index("by_creator", ["creatorId"])
    .index("by_app", ["appId"])
    .index("by_campaign", ["campaignId"])
    .index("by_campaign_and_creator", ["campaignId", "creatorId"])
    .index("by_creator_and_app", ["creatorId", "appId"])
    .index("by_status", ["status"])
    .index("by_creator_and_status", ["creatorId", "status"])
    .index("by_creator_and_app_and_status", ["creatorId", "appId", "status"]),

  contractFormalAgreements: defineTable({
    contractId: v.id("creatorContracts"),
    document: v.id("_storage"), // Original contract document, gets replaced with signed version when complete

    // Store signature fields configuration for this specific contract
    signatureFields: v.array(
      v.object({
        fieldId: v.string(), // unique identifier for this field
        signerType: v.union(v.literal("company"), v.literal("creator")),
        fieldType: v.union(
          v.literal("signature"),
          v.literal("date"),
          v.literal("text"),
        ),
        label: v.string(), // e.g., "Founder Signature", "Contract Date", "Creator Name"
        position: v.object({
          x: v.number(), // x coordinate (percentage)
          y: v.number(), // y coordinate (percentage)
          pageNumber: v.number(), // which page this field is on (1-indexed)
        }),
        size: v.object({
          width: v.number(),
          height: v.number(),
        }),
        required: v.boolean(),
        placeholder: v.optional(v.string()),
      }),
    ),

    status: v.union(
      v.literal("pendingCompanySignature"), // Waiting for company to sign
      v.literal("pendingCreatorSignature"), // Waiting for creator to sign
      v.literal("signed"), // All parties have signed
      v.literal("cancelled"), // Process was cancelled
    ),

    createdBy: v.id("users"), // The founder who initiated the contract

    // Track each signature field completion
    signatureProgress: v.array(
      v.object({
        fieldId: v.string(), // matches template signatureFields.id
        signerType: v.union(v.literal("company"), v.literal("creator")),
        signerUserId: v.optional(v.id("users")), // who actually signed this field
        status: v.union(v.literal("pending"), v.literal("completed")),
        signedAt: v.optional(v.number()),
        value: v.optional(v.string()), // signature data (base64 for signatures, text for other fields)
      }),
    ),

    // Current signer (whose turn it is to sign)
    currentSignerType: v.optional(
      v.union(v.literal("company"), v.literal("creator")),
    ),
  })
    .index("by_contract", ["contractId"])
    .index("by_status", ["status"])
    .index("by_created_by", ["createdBy"])
    .index("by_current_signer", ["currentSignerType"]),

  // Table to track videos created under contracts
  contractVideos: defineTable({
    contractId: v.id("creatorContracts"),
    creatorId: v.id("creators"),
    appId: v.id("apps"),

    // Video review workflow status
    status: v.optional(
      v.union(
        v.literal("ai_reviewing"),
        v.literal("ai_approved"),
        v.literal("ai_rejected"),
        v.literal("team_reviewing"),
        v.literal("team_rejected"),
        v.literal("brand_reviewing"),
        v.literal("approved"), // Final approval state (video is good to go)
        v.literal("rejected"), // Final rejection state

        // deprecated
        v.literal("needs_manual_review"),
      ),
    ),

    // DEPRECATED: These single-text fields are replaced by contractVideoDiscussions table
    // Kept for backward compatibility during migration
    rejectionReason: v.optional(v.string()),
    contestReason: v.optional(v.string()),
    manualReviewReason: v.optional(v.string()),

    // Video details
    url: v.string(),
    title: v.optional(v.string()),
    postDate: v.optional(v.number()),
    platform: v.optional(v.string()), // e.g., "tiktok", "instagram", etc.
    utm: v.optional(v.string()), // link for utm tracking
    downloadUrl: v.optional(v.string()),

    // Metrics
    views: v.optional(v.number()),
    likes: v.optional(v.number()),
    comments: v.optional(v.number()),
    shares: v.optional(v.number()),
    bookmarks: v.optional(v.number()),

    // Earnings
    earnings: v.optional(v.number()),

    // this is for challenge campaigns mainly for now
    creatorInvoice: v.optional(v.union(v.string(), v.id("_storage"))), // url or file
    paidAmount: v.optional(v.number()),
    paymentDate: v.optional(v.number()),
    paymentReceipt: v.optional(v.union(v.string(), v.id("_storage"))), // url or file

    // Cronjob tracking
    lastUpdated: v.optional(v.number()),
  })
    .index("by_contract", ["contractId"])
    .index("by_status", ["status"])
    .index("by_contract_and_status", ["contractId", "status"])
    .index("by_creator", ["creatorId"])
    .index("by_app", ["appId"])
    .index("by_app_and_creator", ["appId", "creatorId"])
    .index("by_app_and_platform", ["appId", "platform"])
    .index("by_url_and_creator", ["url", "creatorId"])
    .index("by_contract_and_creator_and_app", [
      "contractId",
      "creatorId",
      "appId",
    ]),

  // Discussions/feedback for published videos (contractVideos)
  // Replaces the old single-text rejectionReason/contestReason/manualReviewReason fields
  contractVideoDiscussions: defineTable({
    contractVideoId: v.id("contractVideos"),
    author: v.id("users"), // Can be creator or brand
    authorType: v.union(
      v.literal("creator"),
      v.literal("brand"),
      v.literal("team"),
      v.literal("ai"),
    ),
    message: v.string(),
    attachments: v.optional(
      v.array(
        v.object({
          storageId: v.id("_storage"),
          name: v.string(),
          type: v.string(), // MIME type
          size: v.number(),
        }),
      ),
    ),
    // For threaded discussions
    replyTo: v.optional(v.id("contractVideoDiscussions")),
    // Track what action was taken (optional - for audit trail)
    action: v.optional(
      v.union(
        v.literal("rejected"),
        v.literal("approved"),
        v.literal("requested_revision"),
        v.literal("contested"),
        v.literal("comment"),
      ),
    ),
  })
    .index("by_contract_video", ["contractVideoId"])
    .index("by_author", ["author"])
    .index("by_contract_video_and_author", ["contractVideoId", "author"]),

  // Video analytics get updates at least once a day at midnight, but can have
  // more than 1 update per day if the video state changes.
  contractVideoAnalytics: defineTable({
    contractVideoId: v.id("contractVideos"),
    date: v.number(), // unix timestamp
    views: v.optional(v.number()),
    likes: v.optional(v.number()),
    comments: v.optional(v.number()),
    shares: v.optional(v.number()),
    state: v.optional(
      v.union(
        v.literal("ai_reviewing"),
        v.literal("ai_approved"),
        v.literal("ai_rejected"),
        v.literal("team_reviewing"),
        v.literal("team_rejected"),
        v.literal("brand_reviewing"),
        v.literal("approved"),
        v.literal("rejected"),
        // Deprecated - kept for backwards compatibility with old analytics data
        v.literal("needs_manual_review"),
      ),
    ), // current state at time of snapshot
  }).index("by_contract_video", ["contractVideoId"]),

  // Video Projects for giving feedback on a video that hasn't been published yet
  creatorVideoProjects: defineTable({
    contractId: v.id("creatorContracts"),
    title: v.string(),
    contractVideoId: v.optional(v.id("contractVideos")), // link the project to a contract video once it gets published
    plannedPlatforms: v.optional(
      v.array(
        v.object({
          platform: v.union(
            v.literal("youtube"),
            v.literal("tiktok"),
            v.literal("instagram"),
            v.literal("youtube-shorts"),
          ),
          handle: v.string(),
        }),
      ),
    ),
  }).index("by_contract", ["contractId"]),

  creatorVideoVersion: defineTable({
    projectId: v.id("creatorVideoProjects"),
    status: v.union(
      v.literal("draft"), // Creator is working on it
      v.literal("ai_reviewing"),
      v.literal("ai_approved"),
      v.literal("ai_rejected"),
      v.literal("team_reviewing"),
      v.literal("team_rejected"), // Kept for backwards compatibility
      v.literal("needs_review"), // Standardized status for videos needing creator review
      v.literal("brand_reviewing"),
      v.literal("approved"), // Final approval by brand - ready to publish
      v.literal("rejected"), // Final rejection by brand
    ),
    videoStorageId: v.id("_storage"),
  })
    .index("by_project", ["projectId"])
    .index("by_project_and_status", ["projectId", "status"]),

  creatorVideoComments: defineTable({
    versionId: v.id("creatorVideoVersion"),
    author: v.id("users"),
    message: v.string(),
    timestampRange: v.object({
      start: v.number(),
      end: v.optional(v.number()),
    }),
    replyTo: v.optional(v.id("creatorVideoComments")),
    attachments: v.optional(
      v.array(
        v.object({
          storageId: v.id("_storage"),
          name: v.string(),
          type: v.string(), // MIME type
          size: v.number(),
        }),
      ),
    ),
    drawing: v.optional(v.string()), // SVG string for drawings
  }).index("by_version", ["versionId"]),

  chatMessages: defineTable({
    creatorId: v.id("creators"),
    appId: v.id("apps"),
    campaignId: v.optional(v.id("campaigns")),
    senderId: v.union(v.id("creators"), v.id("users")), // creator or founder
    message: v.string(),
    media: v.optional(v.id("_storage")),
  })
    .index("by_campaign_and_creator", ["campaignId", "creatorId"])
    .index("by_creator_and_app", ["creatorId", "appId"])
    .index("by_creator", ["creatorId"])
    .index("by_app", ["appId"]),

  apps: defineTable(appFields),

  appCompetitorVideos: defineTable({
    campaignIds: v.array(v.id("campaigns")), // the campaigns that this video should be shown in
    appId: v.id("apps"),
    videoUrl: v.string(),
    title: v.optional(v.string()),
    likes: v.optional(v.number()),
    comments: v.optional(v.number()),
    shares: v.optional(v.number()),
  }).index("by_app", ["appId"]),

  // Junction table for founders and the apps they own
  appFounders: defineTable({
    userId: v.id("users"),
    appId: v.id("apps"),
    role: v.union(v.literal("admin")), // in case we want to add more roles with different permissions in the future
  })
    .index("by_user", ["userId"])
    .index("by_app", ["appId"])
    .index("by_user_and_app", ["userId", "appId"]), // For quick lookups of specific relationships

  // appFounderInvites
  appFounderInvites: defineTable({
    invitorId: v.id("users"),
    inviteeEmail: v.string(),
    appId: v.id("apps"),
    status: v.union(
      v.literal("pending"),
      v.literal("approved"),
      v.literal("rejected"),
    ),
  })
    .index("by_invitor", ["invitorId"])
    .index("by_invitee_email", ["inviteeEmail"])
    .index("by_invitee_email_and_app", ["inviteeEmail", "appId"])
    .index("by_app", ["appId"]),

  // creatorInvites - for app founders to invite creators with pre-defined contract terms
  creatorInvites: defineTable({
    invitorId: v.id("users"), // app founder who sent the invite
    inviteeEmail: v.string(), // creator's email
    campaignId: v.id("campaigns"),
    appId: v.id("apps"),
    status: v.union(
      v.literal("pending"),
      v.literal("accepted"),
      v.literal("rejected"),
    ),
    // Contract terms that will be applied when accepted
    startDate: v.number(),
    endDate: v.number(),
    currency: currencyValidator,

    requiredVideos: v.object({
      count: v.number(),
      duration: v.number(), // duration in days
    }),

    flatFee: v.optional(
      v.object({
        perVideo: v.optional(v.number()),
        timePeriod: v.optional(
          v.object({
            amount: v.number(),
            days: v.number(), // number of days (for full flexibility if people want to pay out every 10 days or something)
          }),
        ),
      }),
    ),

    cpmRate: v.optional(
      v.union(
        v.object({
          type: v.literal("totalViews"),
          threshold: v.number(), // 10K views
          rate: v.number(), // $2 CPM
          maxPayout: v.optional(v.number()),
          cutoffPeriod: v.number(), // this represents the number of days after the contract ends to calculate the total views to account for videos posted on the last day
        }),
        v.object({
          type: v.literal("perVideo"),
          threshold: v.number(), // 25K views
          rate: v.number(), // $3 CPM
          maxPayout: v.optional(v.number()),
          cutoffPeriod: v.number(), // this represents the number of days after a video is posted to calculate earnings
        }),
      ),
    ),
    // Optional custom message from founder
    customMessage: v.optional(v.string()),
    // Timestamp when responded
    respondedAt: v.optional(v.number()),
  })
    .index("by_invitor", ["invitorId"])
    .index("by_invitee_email", ["inviteeEmail"])
    .index("by_invitee_email_and_campaign", ["inviteeEmail", "campaignId"])
    .index("by_campaign", ["campaignId"])
    .index("by_app", ["appId"])
    .index("by_status", ["status"]),

  notifications: defineTable({
    userId: v.id("users"),
    message: v.string(),
    isRead: v.boolean(),
    redirectTo: v.optional(v.string()),

    appId: v.optional(v.id("apps")), // used to easily show all the notifications across all campaigns
    campaignId: v.optional(v.id("campaigns")), // used to pinpoint the specific campaign this notification is for

    creatorId: v.optional(v.id("creators")), // if the notification is for a creator
  })
    .index("by_user", ["userId"])
    .index("by_userId_and_creatorId", ["userId", "creatorId"])
    .index("by_userId_and_campaignId", ["userId", "campaignId"])
    .index("by_userId_and_appId", ["userId", "appId"]),

  // flow experimental product
  exp__creatorVideos: defineTable({
    creatorId: v.id("exp__creatorProfile"),
    videoUrl: v.string(),
    title: v.string(),
    views: v.number(),
    likes: v.number(),
    commentCount: v.number(),
    // should probably be it's own table
    comments: v.array(
      v.object({
        commentUsername: v.string(),
        commentText: v.string(),
      }),
    ),
    platform: v.string(),
    // should probably be it's own table
    transcript: v.optional(v.string()),
  })
    .index("byVideoId", ["videoUrl"])
    .index("byCreatorId", ["creatorId"]),

  exp__creatorProfile: defineTable({
    creatorId: v.optional(v.id("creators")), // Link to the creator who owns this profile
    handle: v.string(),
    platform: v.string(), // "youtube", "instagram", "tiktok"
    name: v.string(),
    image: v.optional(v.union(v.string(), v.id("_storage"))), // Support both URL strings and storage IDs
    bio: v.string(),
    followers: v.number(),
    videos: v.number(),
    contentNiche: v.optional(v.array(v.string())),
    topContentTopics: v.optional(v.array(v.string())),
    contentStyle: v.optional(v.string()),
    audienceAnalysis: v.optional(v.string()),
  })
    .index("byHandle", ["handle"])
    .index("byHandleAndPlatform", ["handle", "platform"])
    .index("byCreatorId", ["creatorId"]),

  // Table to store liked hooks for gamification and AI improvement
  exp__likedHooks: defineTable({
    creatorId: v.id("exp__creatorProfile"),
    hookText: v.string(),
    hookCategory: v.string(),
    hookReasoning: v.string(),
    potentialPaths: v.optional(v.array(v.string())),
    // Topic/niche labeling
    hookTopic: v.optional(v.string()), // The content topic/niche this hook belongs to
    // Store the original generation context for better AI learning
    originalDirection: v.optional(v.string()), // The custom direction used when generating this hook
    likedAt: v.number(), // Timestamp when liked
    isFilmed: v.optional(v.boolean()), // Whether this hook has been filmed
    filmedAt: v.optional(v.number()), // Timestamp when marked as filmed
    isPosted: v.optional(v.boolean()), // Whether this hook has been posted with video URL
    postedAt: v.optional(v.number()), // Timestamp when marked as posted
    // Script content for liked hooks
    scriptContent: v.optional(v.string()), // Generated script for this hook
    // Video URL and analytics when moved to posted
    videoUrl: v.optional(v.string()), // URL of the posted video
  })
    .index("byCreatorId", ["creatorId"])
    .index("byCreatorAndLikedAt", ["creatorId", "likedAt"])
    .index("byCreatorAndFilmed", ["creatorId", "isFilmed"])
    .index("byVideoUrl", ["videoUrl"]),

  // Table to store video analytics for filmed hooks
  exp__hookVideoAnalytics: defineTable({
    hookId: v.id("exp__likedHooks"), // Reference to the liked hook
    creatorId: v.id("exp__creatorProfile"),
    videoUrl: v.string(),
    platform: v.string(), // "youtube", "tiktok", "instagram", "youtube-shorts"
    title: v.optional(v.string()),
    postDate: v.optional(v.number()),
    views: v.optional(v.number()),
    likes: v.optional(v.number()),
    comments: v.optional(v.number()),
    shares: v.optional(v.number()),
    bookmarks: v.optional(v.number()),
    lastUpdated: v.optional(v.number()), // Last time analytics were fetched
    analyticsHistory: v.optional(
      v.array(
        v.object({
          date: v.number(),
          views: v.optional(v.number()),
          likes: v.optional(v.number()),
          comments: v.optional(v.number()),
          shares: v.optional(v.number()),
          bookmarks: v.optional(v.number()),
        }),
      ),
    ), // Historical analytics data for trend tracking
  })
    .index("byHookId", ["hookId"])
    .index("byCreatorId", ["creatorId"])
    .index("byVideoUrl", ["videoUrl"])
    .index("byCreatorAndPlatform", ["creatorId", "platform"]),

  stripeConnectAccounts: defineTable({
    creatorId: v.id("creators"),
    accountId: v.string(),
  }).index("byCreatorId", ["creatorId"]),

  stripeAffiliateAccounts: defineTable({
    userId: v.id("users"),
    appId: v.id("apps"), // Make affiliate accounts app-specific
    accountId: v.string(),
    accessToken: v.string(),
    refreshToken: v.string(),
    stripePublishableKey: v.string(),
    livemode: v.boolean(),
    scope: v.string(),
    tokenType: v.string(),
    // Timestamps
    tokenExpiresAt: v.optional(v.number()), // Access token expiry (1 hour from creation)
    refreshTokenExpiresAt: v.optional(v.number()), // Refresh token expiry (1 year from creation)
  })
    .index("byUserId", ["userId"])
    .index("byAppId", ["appId"])
    .index("byUserAndApp", ["userId", "appId"]), // Composite index for user + app queries

  customLeaderboards: defineTable({
    name: v.string(),
  }),

  customLeaderboardCreators: defineTable({
    leaderboardId: v.id("customLeaderboards"),
    creatorId: v.id("creators"),
  })
    .index("by_leaderboard", ["leaderboardId"])
    .index("by_creator", ["creatorId"])
    .index("by_leaderboard_and_creator", ["leaderboardId", "creatorId"]),
  // Campaign-level coupon configurations for automatic creator coupon generation
  campaignCouponConfigs: defineTable({
    campaignId: v.id("campaigns"),
    appId: v.id("apps"),
    // Main coupon configuration
    isEnabled: v.boolean(), // Whether affiliate coupons are enabled for this campaign
    discountType: v.union(v.literal("percentage"), v.literal("fixed")), // percentage or fixed amount
    discountValue: v.number(), // percentage (1-100) or fixed amount in cents
    currency: v.optional(v.string()), // Required for fixed amount discounts (USD, EUR, GBP)
    maxRedemptions: v.optional(v.number()), // Optional limit on coupon usage per promotion code
    productIds: v.optional(v.array(v.string())), // Optional array of product IDs to restrict to
    // Affiliate commission configuration
    affiliateCommissionPercentage: v.number(), // Percentage of discounted + tax amount to give to affiliate (1-100)
    // Naming configuration for generated promotion codes
    couponSuffix: v.optional(v.string()), // Optional suffix for generated promotion codes (e.g., "SAVE20")
    // Main Stripe coupon details (NEW)
    stripeCouponId: v.optional(v.string()), // Main coupon ID in Stripe (created once per campaign)
  })
    .index("byCampaign", ["campaignId"])
    .index("byApp", ["appId"])
    .index("byAppAndEnabled", ["appId", "isEnabled"]),

  // Track automatically generated creator promotion codes (UPDATED)
  creatorAffiliatePromotionCodes: defineTable({
    campaignId: v.id("campaigns"),
    creatorId: v.id("creators"),
    appId: v.id("apps"),
    stripeCouponId: v.string(), // Reference to the main Stripe coupon
    stripePromotionCodeId: v.string(), // Stripe promotion code ID
    promotionCode: v.string(), // The actual promotion code (for easy lookup)
    isActive: v.boolean(), // Whether the promotion code is currently active
    usageCount: v.optional(v.number()), // Track usage from Stripe webhooks
    maxRedemptions: v.optional(v.number()), // Individual limit for this promotion code
  })
    .index("byCampaignAndCreator", ["campaignId", "creatorId"])
    .index("byCreator", ["creatorId"])
    .index("byApp", ["appId"])
    .index("byPromotionCode", ["promotionCode"])
    .index("byStripePromotionCodeId", ["stripePromotionCodeId"]),
});

export default schema;
